import { parseCompositeOrderText } from '@/lib/assistant/parseCompositeOrderText';
import { CHECKOUT_PAYMENT_LABELS, getEnabledPaymentMethodsForCheckout } from '@/lib/checkout/paymentMethods';
import { hasCompositeOrderIntent } from '@/lib/assistant/assistantRegionalSpanish';
import { rankProductMatches } from '@/lib/assistant/assistantProductSearch';
import { ORDER_LINE_HINT_RX, looksLikeStaffOrderPaste } from '@/lib/orders/staffTakeOrder';

const PHONE_RX =
  /(?:\+?\s*56\s*)?(?:9\s*\d{4}\s*\d{4}|569\d{8}|9\d{8})\b|\+569\d{8}/g;

const ADDRESS_HINT_RX =
  /\b(calle|av\.|avenida|pasaje|fontt|colina|santiago|región|region)\b/i;

function titleCaseName(s) {
  return String(s || '')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function createEmptyGuidedOrderDraft() {
  return {
    customerName: '',
    customerPhone: '',
    address: '',
    paymentSelection: null,
    cashAmount: null,
    productLines: [],
    parsedLineCount: 0,
  };
}

/** Línea que parece producto del menú (no nombre de cliente). */
export function looksLikeProductOrderLine(text, products, menuData) {
  const raw = String(text || '').trim();
  if (!raw) return false;
  if (/^\d{1,2}[xX×]\s+\S/.test(raw) || /^\d{1,2}\s+\S/.test(raw)) return true;
  if (ORDER_LINE_HINT_RX.test(raw)) return true;
  if (hasCompositeOrderIntent(raw)) return true;
  if (!products?.length) return false;
  const ranked = rankProductMatches(raw, products, menuData);
  if (ranked[0]?.score >= 3.5) return true;
  const parsed = parseCompositeOrderText(raw, products, menuData, { staffOrder: true });
  return (parsed?.lines?.length || 0) > 0;
}

export function extractNameFromGreeting(text, context = {}) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const m = raw.match(
    /\b(?:hola[,!]?\s*)?(?:soy|me llamo|mi nombre es)\s+([a-záéíóúñ]+(?:\s+[a-záéíóúñ]+){0,2})\b/i
  );
  if (m) return titleCaseName(m[1]);
  if (
    /^[a-záéíóúñ]+(?:\s+[a-záéíóúñ]+){0,2}$/i.test(raw) &&
    raw.length <= 40 &&
    !ORDER_LINE_HINT_RX.test(raw) &&
    !PHONE_RX.test(raw) &&
    !looksLikeProductOrderLine(raw, context.products, context.menuData)
  ) {
    return titleCaseName(raw);
  }
  return '';
}

export function extractPhoneFromText(text) {
  const raw = String(text || '').trim();
  const m = raw.match(PHONE_RX);
  if (!m?.[0]) return '';
  let phone = m[0].replace(/\s/g, '');
  if (!phone.startsWith('+')) {
    if (phone.startsWith('569')) phone = `+${phone}`;
    else if (phone.startsWith('9')) phone = `+56${phone}`;
    else phone = `+${phone}`;
  }
  return phone;
}

export function extractPaymentFromText(text) {
  const raw = String(text || '').trim().toLowerCase();
  if (!raw) return null;
  if (/\b(transferencia|transfer)\b/.test(raw)) return 'transferencia';
  if (/\b(efectivo|cash)\b/.test(raw)) return 'efectivo';
  if (/\b(debito|débito|tarjeta|card)\b/.test(raw)) return 'debito';
  if (/\b(mercadopago|mercado pago)\b/.test(raw)) return 'mercadopago';
  return null;
}

export function normalizeAddressLine(text) {
  let a = String(text || '').trim();
  a = a.replace(/^(a|en|direcci[oó]n:?)\s+/i, '').trim();
  return a;
}

export function classifyKnowOrderMessage(text, menuContext = null) {
  const raw = String(text || '').trim();
  if (!raw) return { kind: 'empty' };

  const products = menuContext?.products;
  const menuData = menuContext?.menuData;

  if (/\b(nada\s+m[aá]s|eso\s+es\s+todo|listo\s+as[ií]|no\s+gracias|seguir\s+con\s+el\s+pedido)\b/i.test(raw)) {
    return { kind: 'done' };
  }

  const phone = extractPhoneFromText(raw);
  if (phone && raw.replace(PHONE_RX, '').trim().length < 8) {
    return { kind: 'phone', phone };
  }

  const payment = extractPaymentFromText(raw);
  if (payment && (!ORDER_LINE_HINT_RX.test(raw) || /\bpago\b/i.test(raw))) {
    return { kind: 'payment', payment };
  }

  if (
    ADDRESS_HINT_RX.test(raw) ||
    /\b[a-záéíóúñ][\wáéíóúñ.-]{2,}\s+\d{1,5}/i.test(raw)
  ) {
    return { kind: 'address', address: normalizeAddressLine(raw) };
  }

  if (looksLikeProductOrderLine(raw, products, menuData)) {
    return { kind: 'products', line: raw };
  }

  const name = extractNameFromGreeting(raw, { products, menuData });
  if (name && !/^\d/.test(raw) && !ORDER_LINE_HINT_RX.test(raw)) {
    return { kind: 'name', name };
  }

  return { kind: 'unknown', text: raw };
}

export function businessNeedsDeliveryAddress(business) {
  return Array.isArray(business?.service_types) && business.service_types.includes('delivery');
}

export function getGuidedOrderMissingFields(draft, business) {
  const missing = [];
  const hasProducts = (draft.parsedLineCount || 0) > 0 || (draft.productLines?.length || 0) > 0;
  if (!hasProducts) missing.push('products');
  if (!draft.paymentSelection) missing.push('payment');
  if (businessNeedsDeliveryAddress(business) && !draft.address) missing.push('address');
  return missing;
}

export function promptForGuidedField(field, draft, business = null) {
  const first = String(draft.customerName || '').split(/\s+/)[0] || '';
  switch (field) {
    case 'products':
      return first
        ? `¡Genial ${first}! ¿Qué querés pedir? (ej: 2 hamburguesas y 1 bebida)`
        : '¿Qué querés pedir? Escribí cantidad + producto.';
    case 'payment': {
      const { methodIds } = getEnabledPaymentMethodsForCheckout(business);
      const options = methodIds
        .map((id) => CHECKOUT_PAYMENT_LABELS[id]?.toLowerCase())
        .filter(Boolean)
        .join(', ');
      return options ? `¿Cómo vas a pagar? (${options})` : '¿Cómo vas a pagar?';
    }
    case 'address':
      return '¿A qué dirección te lo mandamos?';
    default:
      return 'Contame un poco más para armar tu pedido.';
  }
}

export function mergeGuidedDraftToStaffText(draft) {
  const lines = [...(draft.productLines || [])];
  if (draft.customerName) lines.push(draft.customerName);
  if (draft.address) lines.push(draft.address);
  if (draft.paymentSelection) {
    lines.push(
      draft.paymentSelection === 'transferencia'
        ? 'pago transferencia'
        : draft.paymentSelection === 'efectivo'
          ? 'pago efectivo'
          : `pago ${draft.paymentSelection}`
    );
  }
  if (draft.customerPhone) lines.push(draft.customerPhone);
  return lines.join('\n');
}

function refreshParsedLineCount(draft, products, menuData) {
  const orderText = (draft.productLines || []).join('\n');
  if (!orderText.trim()) {
    return { ...draft, parsedLineCount: 0 };
  }
  const parsed = parseCompositeOrderText(orderText, products, menuData, { staffOrder: true });
  const count = parsed?.lines?.length || 0;
  return { ...draft, parsedLineCount: count };
}

/**
 * Un turno del flujo guiado «Sé qué pedir» (mensajes parciales).
 * @returns {{ action: 'full_parse'|'prompt'|'finalize'|'need_products', draft: object, prompt?: string, text?: string }}
 */
export function processGuidedOrderTurn({ draft, message, menuData }) {
  const products = menuData?.products || [];
  const business = menuData?.business || {};
  let next = { ...(draft || createEmptyGuidedOrderDraft()) };

  if (looksLikeStaffOrderPaste(message)) {
    return { action: 'full_parse', draft: next, text: String(message || '').trim() };
  }

  const kind = classifyKnowOrderMessage(message, { products, menuData });

  if (kind.kind === 'done') {
    const missing = getGuidedOrderMissingFields(next, business);
    if (missing.includes('products')) {
      return { action: 'need_products', draft: next, prompt: promptForGuidedField('products', next) };
    }
    return { action: 'finalize', draft: next, text: mergeGuidedDraftToStaffText(next) };
  }

  if (kind.kind === 'name' && kind.name) next.customerName = kind.name;
  if (kind.kind === 'phone' && kind.phone) next.customerPhone = kind.phone;
  if (kind.kind === 'payment' && kind.payment) next.paymentSelection = kind.payment;
  if (kind.kind === 'address' && kind.address) next.address = kind.address;
  if (kind.kind === 'products' && kind.line) {
    next.productLines = [...(next.productLines || []), kind.line];
    next = refreshParsedLineCount(next, products, menuData);
  }
  if (kind.kind === 'unknown' && kind.text) {
    const asProducts = parseCompositeOrderText(kind.text, products, menuData, { staffOrder: true });
    if (asProducts?.lines?.length) {
      next.productLines = [...(next.productLines || []), kind.text];
      next = refreshParsedLineCount(next, products, menuData);
    } else if (!next.customerName) {
      const maybeName = extractNameFromGreeting(kind.text, { products, menuData });
      if (maybeName) next.customerName = maybeName;
    }
  }

  const missing = getGuidedOrderMissingFields(next, business);
  if (!missing.length) {
    return { action: 'finalize', draft: next, text: mergeGuidedDraftToStaffText(next) };
  }

  return {
    action: 'prompt',
    draft: next,
    prompt: promptForGuidedField(missing[0], next, business),
  };
}
